import React from 'react'
import { compact } from 'lodash'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { useSelector } from 'react-redux'
import { defineMessages } from 'react-intl'
import { useFormatMessage } from 'src/providers/intl'

// Selectors
import {
  selectClientHomes,
  selectClientId,
  selectClientIsGuest,
} from 'src/store/selectors/client'
import {
  routeToHome,
  selectRouteToNewHomeNav,
} from 'src/store/selectors/routes'
import { selectCustomerId } from 'src/store/selectors/customerProfile'

// Components
import {
  DropdownSelect,
  DropdownItem,
  DropdownItemDivider,
  If,
  Unless,
  Button,
} from 'src/Components'

// Hooks
import { useUniqueId } from 'src/hooks'

// Utils
import history from 'src/history'
import {
  PATH_HELPERS,
  YOUR_HOME_DROPDOWN_TAB_NAMES,
} from 'src/constants/navigation'

// Scss
import styles from './YourHome.module.scss'

const propTypes = {
  activeDropdownItem: PropTypes.string,
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium']),
}

const YOUR_HOME_DROPDOWN_SELECT = {
  default: defineMessages({
    label: {
      id: 'Navbar.yourHome.dropdownSelect.label',
      defaultMessage: 'Your Home',
    },
    addHomeLabel: {
      id: 'Navbar.yourHome.dropdownSelect.addHomeLabel',
      defaultMessage: 'Add Home',
    },
  }),
}

const YourHome = ({ activeDropdownItem, disabled, size }) => {
  const intl = useFormatMessage()
  const clientId = useSelector(selectClientId)

  const homes = useSelector(selectClientHomes)
  const routeToAddOrFirstHome = useSelector(selectRouteToNewHomeNav)
  const tagKey = useUniqueId('newHomeButton_')
  const dividerKey = useUniqueId('dividerKey_')

  const customerProfileId = useSelector(selectCustomerId)
  const isGuest = useSelector(selectClientIsGuest)

  const handleRouteToAddHome = () => {
    const routeToAddHome = routeToHome(clientId, 'new')
    history.push(routeToAddHome)
  }

  const handleRouteToExistingHome = homeId => {
    const homeRoute = routeToHome(clientId, homeId)
    history.push(homeRoute)
  }

  const handlePrimaryAuthedClick = () => {
    history.push(routeToAddOrFirstHome)
  }

  const handlePrimaryUnAuthedClick = () => {
    window.location.assign(PATH_HELPERS.purl.buildPath(customerProfileId))
  }

  const renderHomeNavDropDownItems = () => {
    let homeItems = []

    if (homes) {
      homeItems = homes.map(home => (
        <DropdownItem
          className={classnames('dropdownItem', `dropdownItem-${home.id}`)}
          id={home.id}
          key={home.id}
          label={home.address.street}
          onClick={() => handleRouteToExistingHome(home.id)}
          value={home.id}
        />
      ))
    }

    const addHomeLink = (
      <DropdownItem
        className='dropdownItem'
        id={YOUR_HOME_DROPDOWN_TAB_NAMES.ADD_HOME}
        key={tagKey}
        label={intl(YOUR_HOME_DROPDOWN_SELECT.default.addHomeLabel)}
        onClick={handleRouteToAddHome}
        value={YOUR_HOME_DROPDOWN_TAB_NAMES.ADD_HOME}
      />
    )

    const divider = <DropdownItemDivider key={dividerKey} />

    const dropDownItems = [
      ...homeItems,
      homes && homes.length ? divider : null,
      addHomeLink,
    ]

    return compact(dropDownItems)
  }

  return (
    <>
      <If condition={isGuest}>
        <div className={styles.container}>
          <Button id='purl' onClick={handlePrimaryUnAuthedClick}>
            {intl(YOUR_HOME_DROPDOWN_SELECT.default.label)}
          </Button>
        </div>
      </If>
      <Unless condition={isGuest}>
        <DropdownSelect
          className='dropdownSelect'
          dataqa='yourHome'
          disabled={disabled}
          label={intl(YOUR_HOME_DROPDOWN_SELECT.default.label)}
          onClick={handlePrimaryAuthedClick}
          selectedDropdownItem={activeDropdownItem}
          size={size}
        >
          {renderHomeNavDropDownItems()}
        </DropdownSelect>
      </Unless>
    </>
  )
}

YourHome.propTypes = propTypes

export default YourHome
